import { Box, Grid, Stack, useMediaQuery, useTheme } from "@mui/material";
import LanguageContext from "components/LanguageContext";
import Wrapper from "components/LayoutFront/Wrapper";
import MessageContext from "components/MessageContext";
import SiteContext from "components/SiteContext";
import { frenchPhoneCodes } from "components/templates/webcallback/phoneCodes";
import WebcallbackGridSlots from "components/templates/webcallback/WebcallbackGridSlots";
import WebcallbackIcon from "components/templates/webcallback/WebcallbackIcon";
import WebcallbackMainBlock from "components/templates/webcallback/WebcallbackMainBlock";
import WebcallbackSubBlock from "components/templates/webcallback/WebcallbackSubBlock";
import Accordion from "components/templatesComponents/Accordion";
import Button from "components/templatesComponents/Button";
import DSFRModal from "components/templatesComponents/DSFRModal";
import InputDSFR from "components/templatesComponents/InputDSFR";
import SelectDSFR from "components/templatesComponents/SelectDSFR";
import { buttonStyled } from "components/templatesComponents/StyledComponent";
import { useGetAvailability, useGetCalendar, useGetConfiguration, useWebcallback } from "hooks/akio.hook";
import { useFindPages } from "hooks/page.hook";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import {
  formatDateToLocalTimezone,
  fullFormatDate,
  longFormatDate,
  shortFormatDate,
  timeFormat,
} from "utils/commonUtils";
import t from "utils/locales/translation.json";
import webcallBackAvailabilityStatuses from "utils/webcallBackAvailabilityStatuses";

const formatDateForAkio = (date) => {
  if (date) {
    const isoDate = new Date(date).toISOString();
    return isoDate;
  }
  return "";
};

const Webcallback = (props) => {
  const { page: pageVersion } = props;
  const { page, contents } = pageVersion || {};
  const { parentId } = page || {};
  const { CallbackReasonsList } = contents;
  let { CallbackReasons = [] } = CallbackReasonsList || {};
  if (CallbackReasons && !Array.isArray(CallbackReasons)) {
    CallbackReasons = [CallbackReasons];
  }
  CallbackReasons = CallbackReasons.filter((cr) => cr && cr.title && cr.value);

  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up("lg"));

  const [form, setForm] = useState({
    reason: null,
    startDate: "",
    phoneCode: { name: "France métropolitaine", alphaCode: "FR", internationalCode: "+33" },
    phone: "",
  });
  const [reasonSubmitted, setReasonSubmitted] = useState(false);
  const [intervalSlots, setIntervalSlots] = useState({ start: 0, end: 5 });
  const [seeMoreSlots, setSeeMoreSlots] = useState(isMobile);
  const [hasMoreSlots, setHasMoreSlots] = useState(false);
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [refererId, setRefererId] = useState("");
  const phoneCodeModalRef = useRef(null);

  const { reason, startDate, phoneCode, phone } = form;
  const { name, alphaCode, internationalCode } = phoneCode;

  const { language } = useContext(LanguageContext);
  const { displayError } = useContext(MessageContext);
  const site = useContext(SiteContext);
  const { id: siteId } = site || {};

  const {
    data: config = {},
    isError: isErrorConfiguration,
    isFetching: isFetchingConfiguration,
  } = useGetConfiguration(refererId);

  const { webboxRule = {} /* countries = [] */ } = config;
  const { conditions = [] } = webboxRule;
  const { uuid: conditionUuids = "" } = conditions[0] || {};

  // en attente de la fonctionnalité de filtrage des pays côté akio
  // const filteredCountries = useMemo(() => {
  //   if (countries.length === 0) return [];
  //   const frenchCountries = countries.filter((c) => frenchPhoneCodes.map((fcp) => fcp.alphaCode).includes(c.alphaCode));
  //   const neighboringCountries = countries.filter((c) =>
  //     neighboringCountryPhoneCodes.map((ncp) => ncp.alphaCode).includes(c.alphaCode)
  //   );
  //   const europeanCountries = countries.filter((c) =>
  //     europeanPhoneCodes.map((ecp) => ecp.alphaCode).includes(c.alphaCode)
  //   );
  //   const allEuropeanCountries = [...frenchCountries, ...neighboringCountries, ...europeanCountries];
  //   const otherCountries = countries.filter((c) => allEuropeanCountries.every((fc) => fc.alphaCode !== c.alphaCode));
  //   const allCountries = [frenchCountries, neighboringCountries, europeanCountries, otherCountries].filter(
  //     (countriesArray) => countriesArray.length > 0
  //   );
  //   return allCountries;
  // }, [countries]);

  const {
    data: availability = [],
    isError: isErrorAvailability,
    isFetching: isFetchingAvailability,
  } = useGetAvailability(refererId, conditionUuids);

  const { media = {} } = availability[0] || {};
  const { status: availabilityStatus } = media || {};

  const availableToCallback = useMemo(
    () => availabilityStatus === webcallBackAvailabilityStatuses.OPEN_DELAYED_WEBCALLBACK,
    [availabilityStatus],
  );

  const { data: calendarData = {}, isError: isErrorCalendar } = useGetCalendar(refererId, conditionUuids);
  const { timeSlots: timeSlotsData = [] } = calendarData || {};

  const availableTimeSlots = useMemo(() => timeSlotsData.filter((slot) => slot.available) || [], [timeSlotsData]);

  const timeSlots = useMemo(
    () => availableTimeSlots.map((slot) => formatDateToLocalTimezone(slot.fromDate)),
    [availableTimeSlots],
  );

  const { mutate: webcallbackMutate } = useWebcallback({
    onError: (error) => {
      const errorData = error.response.data;
      if (errorData?.error === "ALREADY_EXISTS") {
        displayError(t[language].webcallback.phone_already_exists_error_message);
      } else if (errorData?.error === "SLOT_FULL") {
        displayError(t[language].webcallback.slot_full_error_message);
      } else if (errorData?.error.includes("Invalid Phone Code or Number")) {
        displayError(t[language].webcallback.phone_invalid_error_message);
      } else {
        displayError(t[language].webcallback.unknown_error_message);
      }
    },
    onSuccess: () => {
      setIsFormValid(true);
    },
  });

  const endDate = useMemo(() => {
    if (startDate) {
      const endDateTimestamp =
        availableTimeSlots.find((slot) => slot.fromDate === new Date(startDate).getTime())?.toDate || "";
      return formatDateToLocalTimezone(endDateTimestamp);
    }
    return "";
  }, [availableTimeSlots, startDate]);

  const isAkioError = useMemo(
    () => isErrorConfiguration || isErrorAvailability || isErrorCalendar,
    [isErrorAvailability, isErrorCalendar, isErrorConfiguration],
  );

  const isAvailable = useMemo(
    () => (!availabilityStatus || availableToCallback) && !isAkioError,
    [availabilityStatus, availableToCallback, isAkioError],
  );

  const { data: helpPages = [] } = useFindPages({
    siteId,
    pageId: parentId,
    filters: [`page.lang||eq||${language}`],
  });

  const errorReason = useMemo(() => reasonSubmitted && !reason, [reasonSubmitted, reason]);
  const errorPhoneCode = useMemo(() => form.phoneCode.name === "others", [form.phoneCode.name]);

  const errorPhone = useMemo(
    () => phoneSubmitted && form.phoneCode.name !== "others" && (!phone || !isValidPhoneNumber(phone, alphaCode)),
    [phoneSubmitted, phone, alphaCode, form.phoneCode.name],
  );

  const displayRegistrationProcess = useMemo(
    () =>
      isAvailable &&
      !(reasonSubmitted && reason?.value?.startsWith("error")) &&
      !(phone && phoneSubmitted && isValidPhoneNumber(phone, alphaCode) && isFormValid),
    [isAvailable, reasonSubmitted, reason?.value, phone, phoneSubmitted, alphaCode, isFormValid],
  );

  const displaySuccessBlock = useMemo(
    () => startDate && phone && phoneCode && phoneSubmitted && isFormValid,
    [startDate, phone, phoneCode, phoneSubmitted, isFormValid],
  );

  const availableDateSlots = useMemo(() => {
    const dateSlots = [];
    timeSlots.forEach((slot) => {
      if (!dateSlots.find((d) => d === slot.split("T")[0])) {
        dateSlots.push(slot.split("T")[0]);
      }
    });
    return dateSlots;
  }, [timeSlots]);

  const availableDateSlotsToDisplay = useMemo(
    () => availableDateSlots.slice(intervalSlots.start, intervalSlots.end),
    [availableDateSlots, intervalSlots.end, intervalSlots.start],
  );

  const displayIcons = useMemo(() => availableDateSlots.length > 5, [availableDateSlots.length]);

  const timeSlotsToDisplay = useCallback(
    (ds) => {
      const slots = timeSlots.filter((slot) => slot.split("T")[0] === ds);
      if (slots.length > 8 && !hasMoreSlots) {
        setHasMoreSlots(true);
      }
      return seeMoreSlots ? slots : slots.slice(0, 8) || [];
    },
    [hasMoreSlots, seeMoreSlots, timeSlots],
  );

  const errorPhoneCodeMessage = useMemo(
    () => (
      <Box className="fr-messages-group" id="select-phoneCode-message" aria-live="polite">
        <p className="fr-message fr-message--error" id="select-phoneCode-error">
          {t[language].webcallback.phone_code_other_message}
        </p>
      </Box>
    ),
    [language],
  );

  const phoneToDisplay = useMemo(() => {
    if (phone && isValidPhoneNumber(phone, alphaCode)) {
      return parsePhoneNumber(phone, alphaCode)?.formatInternational();
    }
    return `${internationalCode} ${phone}`;
  }, [phone, alphaCode, internationalCode]);

  const confirmationTitle = useMemo(
    () =>
      `${t[language].webcallback.confirmation_title_1}${phoneToDisplay}, ${
        t[language].webcallback.confirmation_title_2
      }${fullFormatDate({ date: startDate, lang: language })}${
        t[language].webcallback.confirmation_title_3
      }${timeFormat({ date: startDate, lang: language })} ${t[language].webcallback.and} ${timeFormat({
        date: endDate,
        lang: language,
      })}`,
    [language, phoneToDisplay, startDate, endDate],
  );

  const scrollSent = () => {
    scrollIntoView(document.getElementById("breadcrumb"), {
      block: "start",
      inline: "start",
      behavior: "smooth",
    });
  };

  const handleFormChange = (e) => {
    const { name: fieldName } = e.target;
    let { value } = e.target;
    if (fieldName === "reason") {
      setReasonSubmitted(false);
      value = CallbackReasons.find((subject) => subject.title === value);
    }
    if (fieldName === "phoneCode") {
      if (value === "others") {
        phoneCodeModalRef.current.openModal();
        value = { name: value };
      } else {
        value = frenchPhoneCodes.find((ph) => ph.name === value);
      }
    }
    if (fieldName === "startDate") {
      scrollSent();
    }
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const handleClosePhoneCodeModal = () => {
    phoneCodeModalRef.current.closeModal();
  };

  const handleValidateReason = () => {
    setReasonSubmitted(true);
    setHasMoreSlots(false);
    if (reason.value && !reason.value.startsWith("error")) {
      setRefererId(reason.value);
    }
  };

  const goBackToReasonSelection = () => {
    setSeeMoreSlots(isMobile);
    setReasonSubmitted(false);
    setForm({ ...form, reason: null });
  };

  const handleFormValidate = () => {
    setPhoneSubmitted(true);
    if (phone && isValidPhoneNumber(phone, alphaCode)) {
      const bodyParams = {
        refererId,
        phoneNumber: phone.replace(/\s+/g, ""),
        callbackTime: formatDateForAkio(startDate),
        callbackTimeEnd: formatDateForAkio(endDate),
        targetingConditionUuid: conditionUuids,
        countryCode: alphaCode,
      };
      webcallbackMutate(bodyParams);
      scrollSent();
    }
  };

  return (
    <Wrapper sx={{ px: { xs: 2, lg: 0 } }}>
      <Stack>
        {!isAvailable && (
          <WebcallbackMainBlock
            title={t[language].webcallback.no_slot_title}
            subtitle={t[language].webcallback.no_slot_subtitle}
            icon="calendar-unavailable"
            pageToReturn={helpPages[0]}
          />
        )}
        {displayRegistrationProcess && (
          <>
            <WebcallbackMainBlock title={t[language].webcallback.title} subtitle={t[language].webcallback.subtitle} />
            {/* FIRST STEP (Reason) */}
            <WebcallbackSubBlock
              isStepValidated={!!(reason?.title && reasonSubmitted && availableToCallback)}
              hasDate={!!startDate}
            >
              <Stack spacing={3} sx={{ flex: 1, width: "100%" }}>
                <p className="fr-text--lg fr-text--bold">{t[language].webcallback.reason_title}</p>
                {reason?.title && reasonSubmitted && availableToCallback ? (
                  <p className="fr-text--lg">{reason?.title}</p>
                ) : (
                  <Box className={`fr-select-group${errorReason ? " fr-select-group--error" : ""} fr-mb-0`}>
                    <label className="fr-label" htmlFor="select-reason">
                      {t[language].webcallback.select_label}
                    </label>
                    <SelectDSFR
                      name="reason"
                      id="select-reason"
                      value={reason?.title}
                      onChange={handleFormChange}
                      error={errorReason}
                      errorMessage={t[language].webcallback.select_error_message}
                      Wrapper={CallbackReasonsList.Wrapper}
                      wrapperProps={CallbackReasonsList.wrapperProps}
                    >
                      <option value="" selected disabled>
                        {t[language].webcallback.select_placeholder}
                      </option>
                      {CallbackReasons.map((opt) => (
                        <option value={opt?.title} key={opt.id}>
                          {opt?.title}
                        </option>
                      ))}
                    </SelectDSFR>
                  </Box>
                )}
              </Stack>
              {!(reason && reasonSubmitted && availableToCallback) && (
                <Button
                  onClick={handleValidateReason}
                  disabled={isFetchingConfiguration || isFetchingAvailability}
                  sx={{
                    ...buttonStyled,
                    height: "fit-content",
                    mb: { lg: errorReason ? "36px !important" : 0 },
                  }}
                >
                  {t[language].webcallback.reason_button_label}
                </Button>
              )}
              {reason && reasonSubmitted && !startDate && availableToCallback && (
                <button
                  type="button"
                  className="fr-btn fr-icon-arrow-go-back-line fr-btn--icon-left fr-btn--tertiary-no-outline"
                  onClick={goBackToReasonSelection}
                >
                  {t[language].webcallback.edit_button_label}
                </button>
              )}
            </WebcallbackSubBlock>
            {/* SECOND STEP (Date) */}
            {reason?.title && reasonSubmitted && availableToCallback && (
              <WebcallbackSubBlock isStepValidated={!!startDate}>
                <Stack spacing={3} sx={{ flex: 1, width: "100%" }}>
                  <p className="fr-text--lg fr-text--bold">{t[language].webcallback.moment_title}</p>
                  {startDate ? (
                    <p className="fr-text--lg">{`${t[language].webcallback.day_prefix} ${longFormatDate({
                      date: startDate,
                      lang: language,
                    })} ${t[language].webcallback.hour_prefix} ${timeFormat({
                      date: startDate,
                      lang: language,
                    })}`}</p>
                  ) : (
                    <>
                      <p>
                        {timeSlots?.length} {t[language].webcallback.available_slots}
                      </p>
                      <Stack direction="row" spacing={2} sx={{ display: { xs: "none", lg: "flex" } }}>
                        {displayIcons && (
                          <WebcallbackIcon
                            iconDSFR="arrow-left-s-line"
                            disabled={intervalSlots.start === 0}
                            handleChange={() =>
                              setIntervalSlots({ start: intervalSlots.start - 1, end: intervalSlots.end - 1 })
                            }
                          />
                        )}
                        <Grid container spacing={3} columns={5} className="fr-mt-n3w fr-ml-n1w" sx={{ flex: 1 }}>
                          {availableDateSlotsToDisplay.map((dateSlot) => (
                            <Grid key={dateSlot} size={{ lg: 1 }}>
                              <Stack spacing={1}>
                                <Box
                                  component="p"
                                  className="fr-text--bold"
                                  sx={{
                                    p: 1,
                                    bgcolor: "var(--background-alt-grey)",
                                    textTransform: "capitalize",
                                    textAlign: "center",
                                  }}
                                >
                                  {shortFormatDate({ date: dateSlot, lang: language })}
                                </Box>
                                <WebcallbackGridSlots
                                  timeSlotsToDisplay={timeSlotsToDisplay(dateSlot)}
                                  handleChange={handleFormChange}
                                  language={language}
                                />
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                        {displayIcons && (
                          <WebcallbackIcon
                            iconDSFR="arrow-right-s-line"
                            disabled={intervalSlots.end >= availableDateSlots.length}
                            handleChange={() =>
                              setIntervalSlots({ start: intervalSlots.start + 1, end: intervalSlots.end + 1 })
                            }
                          />
                        )}
                      </Stack>
                      <Box
                        sx={{
                          display: { lg: "none" },
                          "& .fr-accordion": {
                            "&:before": { content: "none" },
                            borderTop: "1px solid var(--border-default-grey)",
                          },
                          "& .accordion-item:last-of-type": {
                            borderBottom: "1px solid var(--border-default-grey)",
                          },
                          "& .fr-accordion__button": { textTransform: "capitalize" },
                        }}
                      >
                        <Accordion
                          panels={availableDateSlots.map((dateSlot) => ({
                            id: dateSlot,
                            summary: shortFormatDate({ date: dateSlot, lang: language }),
                            details: (
                              <WebcallbackGridSlots
                                timeSlotsToDisplay={timeSlotsToDisplay(dateSlot)}
                                handleChange={handleFormChange}
                                language={language}
                              />
                            ),
                          }))}
                        />
                      </Box>
                    </>
                  )}
                  {!isMobile && hasMoreSlots && !startDate && (
                    <Button
                      size="sm"
                      onClick={() => setSeeMoreSlots(!seeMoreSlots)}
                      sx={{ ...buttonStyled, alignSelf: "center" }}
                    >
                      {
                        t[language].webcallback[
                          `${seeMoreSlots ? "see_less_slots_button_label" : "see_more_slots_button_label"}`
                        ]
                      }
                    </Button>
                  )}
                </Stack>
                {startDate && (
                  <button
                    type="button"
                    className="fr-btn fr-icon-arrow-go-back-line fr-btn--icon-left fr-btn--tertiary-no-outline"
                    onClick={() => {
                      setForm({ ...form, startDate: "" });
                    }}
                  >
                    {t[language].webcallback.edit_button_label}
                  </button>
                )}
              </WebcallbackSubBlock>
            )}
            {/* THIRD STEP (Phone) */}
            {startDate && (
              <WebcallbackSubBlock>
                <Stack spacing={3} sx={{ flex: 1, width: "100%" }}>
                  <p className="fr-text--lg fr-text--bold">{t[language].webcallback.number_title}</p>
                  <fieldset
                    className="fr-fieldset"
                    id="phone-fieldset"
                    role="group"
                    aria-labelledby="phone-fieldset-legend phone-fieldset-fieldset-messages"
                  >
                    <legend className="fr-fieldset__legend  pl-0 ml-0" id="phone-fieldset-legend">
                      {t[language].webcallback.number_subtitle}
                    </legend>
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      spacing={2}
                      sx={{
                        alignItems: errorPhone ? { xs: "flex-start", lg: "center" } : { xs: "center", lg: "flex-end" },
                        flex: 1,
                      }}
                    >
                      <Box
                        className={`fr-select-group${errorPhoneCode ? " fr-select-group--error" : ""}`}
                        sx={{
                          flex: 1,
                          width: "100%",
                          mb: { lg: errorPhone ? "34px !important" : "0 !important" },
                          "&:before": isMobile ? {} : { bottom: language === "EN" ? -28 : -48 },
                        }}
                      >
                        <label className="fr-label" htmlFor="select-phoneCode">
                          {t[language].webcallback.phone_code_label}
                          <span className="fr-hint-text">{t[language].webcallback.phone_code_hint}</span>
                        </label>
                        <SelectDSFR name="phoneCode" id="select-phoneCode" value={name} onChange={handleFormChange}>
                          {frenchPhoneCodes.length > 0 &&
                            frenchPhoneCodes.map((opt) => (
                              <option value={opt.name} key={opt.alphaCode} className="fr-text--sm">
                                {opt.alphaCode} - {t[language].webcallback.french_countries[opt.alphaCode]} (
                                {opt.internationalCode})
                              </option>
                            ))}
                          <hr />
                          <option value="others" key="others" className="fr-text--sm">
                            {t[language].webcallback.phone_code_other_option}
                          </option>
                        </SelectDSFR>
                        {errorPhoneCode && <Box sx={{ display: { lg: "none" } }}>{errorPhoneCodeMessage}</Box>}
                      </Box>
                      <Box sx={{ flex: 2, width: "100%" }}>
                        <InputDSFR
                          label={t[language].webcallback.phone_label}
                          hint={t[language].webcallback.phone_hint}
                          name="phone"
                          value={phone}
                          onChange={handleFormChange}
                          autoComplete="tel"
                          title={errorPhoneCode ? t[language].webcallback.phone_title : undefined}
                          disabled={errorPhoneCode}
                          error={errorPhone}
                          errorMessage={t[language].webcallback.phone_error_message}
                        />
                      </Box>
                      <Button
                        onClick={handleFormValidate}
                        disabled={errorPhoneCode}
                        sx={{
                          ...buttonStyled,
                          height: "fit-content",
                          mt: { lg: errorPhone ? "21px !important" : 0 },
                        }}
                      >
                        {t[language].webcallback.phone_button_label}
                      </Button>
                    </Stack>
                    {errorPhoneCode && (
                      <Box sx={{ display: { xs: "none", lg: "block" }, mt: 1 }}>{errorPhoneCodeMessage}</Box>
                    )}
                  </fieldset>
                </Stack>
              </WebcallbackSubBlock>
            )}
          </>
        )}
        <DSFRModal onClose={handleClosePhoneCodeModal} size={{ md: 8, lg: 6 }}>
          <div className="fr-modal__content">
            <h1 id="fr-modal-title-modal-cookies" className="fr-modal__title">
              {t[language].webcallback.modal_title}
            </h1>
            <p className="fr-text--md">
              {t[language].webcallback.modal_description_1}
              <Box
                component="a"
                href="tel:+33970830707"
                sx={{ color: "var(--text-title-grey)", display: "inline-block" }}
              >
                09 70 83 07 07
              </Box>
              {t[language].webcallback.modal_description_2}
            </p>
          </div>
        </DSFRModal>
        {/* SUCCESS */}
        {displaySuccessBlock && (
          <WebcallbackMainBlock
            title={confirmationTitle}
            subtitle={t[language].webcallback.confirmation_subtitle}
            icon="calendar"
            pageToReturn={helpPages[0]}
          />
        )}
        {/* Choix "L'accès à mon compte" */}
        {reasonSubmitted && reason?.value === "errorAccount" && (
          <WebcallbackMainBlock
            title={t[language].webcallback.account_title}
            icon="calendar-unavailable"
            pageToReturn={helpPages[0]}
          />
        )}
        {/* Choix "Je n'ai pas de passeport ou de carte d'identité" */}
        {reasonSubmitted && reason?.value === "errorPassport" && (
          <WebcallbackMainBlock
            title={t[language].webcallback.passport_block_title}
            subtitle={t[language].webcallback.passport_block_subtitle}
            icon="calendar-unavailable"
            pageToReturn={helpPages[0]}
          />
        )}
        {/* Choix "France Identité" */}
        {reasonSubmitted && reason?.value === "errorFI" && (
          <WebcallbackMainBlock
            title={t[language].webcallback.fin_title}
            icon="calendar-unavailable"
            pageToReturn={helpPages[0]}
            url="support@france-identite.gouv.fr"
          />
        )}
      </Stack>
    </Wrapper>
  );
};

Webcallback.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default Webcallback;
